'use client';

import type { VariablesOf } from '@graphql-typed-document-node/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNil, throttle } from 'es-toolkit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { graphql, unmaskFragment } from 'src/gql';

import { DataTableExportModal } from '@savvy/data-table';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { AccountsTableColumn } from './utils/accounts-columns';
import { AccountsDataTable, AccountsDataTableFragment } from './accounts-data-table';
import { AccountsDataTableRowFragment } from './accounts-data-table-columns';
import { AccountsPaginationFooterFragment } from './accounts-pagination-footer';
import { AccountsTableHeader } from './accounts-table-header';
import {
  getActiveAccountFilterPresetId,
  setActiveAccountFilterPresetId,
  useSavedAccountFilters,
} from './hooks/use-saved-account-filters';
import { SavedAccountFilterChips } from './saved-account-filter-chips';
import { useAccountsExport } from './use-accounts-export';
import { getDefaultAccountsColumns } from './utils/accounts-columns';
import { useAccountsParams } from './utils/accounts-url-state';

const SORT_BY_MAP = {
  displayName: 'ACCOUNT_NAME',
  displayBalance: 'BALANCE',
  household: 'HOUSEHOLD_NAME',
  startDate: 'START_DATE',
  cashAndMoneyMarketAmount: 'CASH_AND_MONEY_MARKET_AMOUNT',
  cashAndMoneyMarketPercent: 'CASH_AND_MONEY_MARKET_PERCENT',
} as const;

const SORT_DIRECTION_MAP = {
  asc: 'ASC',
  desc: 'DESC',
} as const;

const AdvisorAccountsQuery = graphql(/* GraphQL */ `
  query AdvisorAccountsPageEmbedded(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $advisorIds: [ID!]
    $balanceRange: BalanceRangeInput
    $strategyFilter: StrategyFilterInput
    $feeSchedules: [String!]
    $householdIds: [ID!]
    $includeUnassignedAdvisor: Boolean
    $portfolioMustBePresent: Boolean
    $providerNames: [String!]
    $sortField: AccountSortValues
    $sortDirection: SortDirection
    $query: String
    $showCashMm: Boolean = false
  ) {
    ...AccountsDataTable
  }
`);

// NOTE: Accounts tab has no stat cards because the advisorAccounts query doesn't
// return aggregate fields (totalBalance, totalCount, swimEnrolledCount). Before
// adding stat cards here, the backend query needs to expose those aggregates —
// client-side totals from a paginated page would be misleading.
export function AccountsContent() {
  const { flagCashDashboard: cashDashboardEnabled } = useFlags();
  const {
    params,
    pagination,
    setColumns,
    setBalanceRange,
    setStrategyType,
    setStrategy,
    setFeeSchedule,
    setHousehold,
    setAdvisorIds,
    setIncludeUnassignedAdvisor,
  } = useAccountsParams();
  const { savedFilters, atLimit, saveFilter, updateFilter, renameFilter, deleteFilter } = useSavedAccountFilters();

  const [activeFilterId, setActiveFilterId] = useState<string | null>(() => {
    const id = getActiveAccountFilterPresetId();
    if (id === null) return null;

    const preset = savedFilters.find((savedFilter) => savedFilter.id === id);
    if (preset === undefined) return null;

    return id;
  });

  const selectedColumns = useMemo<AccountsTableColumn[]>(() => {
    let columns = params.columns.length > 0 ? params.columns : getDefaultAccountsColumns();
    if (cashDashboardEnabled !== true) {
      columns = columns.filter((c) => c !== 'Cash & Money Market');
    }
    if (!columns.includes('Account Name')) {
      return ['Account Name', ...columns];
    }
    return columns;
  }, [params.columns, cashDashboardEnabled]);

  const handleColumnsChange = (newColumns: AccountsTableColumn[]) => {
    setColumns(newColumns);
  };

  const showCashMm = useMemo(() => selectedColumns.includes('Cash & Money Market'), [selectedColumns]);

  const variables: VariablesOf<typeof AdvisorAccountsQuery> = useMemo(
    () => ({
      first: pagination.first,
      after: pagination.after,
      last: pagination.last,
      before: pagination.before,
      balanceRange: {
        min: params.balanceMin?.toString(),
        max: params.balanceMax?.toString(),
      },
      strategyFilter: {
        strategyNames: params.strategy,
        strategyTypes: params.strategyType,
      },
      feeSchedules: params.feeSchedule,
      householdIds: params.household,
      advisorIds: params.advisorIds,
      includeUnassignedAdvisor: params.includeUnassignedAdvisor,
      query: params.search,
      portfolioMustBePresent: params.portfolioMustBePresent,
      providerNames: params.providerNames,
      sortField: SORT_BY_MAP[params.sortBy ?? 'displayName'],
      sortDirection: SORT_DIRECTION_MAP[params.sortByOrder ?? 'asc'],
      showCashMm,
    }),
    [params, pagination, showCashMm],
  );

  const [throttledVariables, setThrottledVariables] = useState(variables);

  const throttledSetVariables = useMemo(() => throttle(setThrottledVariables, 250), []);

  useEffect(() => {
    throttledSetVariables(variables);
  }, [variables, throttledSetVariables]);

  const { data, loading, error } = useQueryWithErrorLogging(AdvisorAccountsQuery, {
    variables: throttledVariables,
  });

  const accountsTableData = useMemo(() => unmaskFragment(AccountsDataTableFragment, data), [data]);

  const accounts = useMemo(() => {
    if (isNil(accountsTableData?.advisorAccounts.edges)) return [];
    return accountsTableData.advisorAccounts.edges
      .map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => !isNil(node))
      .map((node) => unmaskFragment(AccountsDataTableRowFragment, node));
  }, [accountsTableData]);

  const paginationData = useMemo(
    () =>
      !isNil(accountsTableData?.advisorAccounts)
        ? unmaskFragment(AccountsPaginationFooterFragment, accountsTableData.advisorAccounts)
        : null,
    [accountsTableData],
  );
  const totalCount = paginationData?.totalCount ?? 0;

  const currentFilters = useMemo(
    () => ({
      balanceMin: params.balanceMin ?? null,
      balanceMax: params.balanceMax ?? null,
      strategyType: params.strategyType,
      strategy: params.strategy,
      feeSchedule: params.feeSchedule,
      household: params.household,
      advisorIds: params.advisorIds,
      includeUnassignedAdvisor: params.includeUnassignedAdvisor,
    }),
    [
      params.balanceMin,
      params.balanceMax,
      params.strategyType,
      params.strategy,
      params.feeSchedule,
      params.household,
      params.advisorIds,
      params.includeUnassignedAdvisor,
    ],
  );

  const isModified = useMemo(() => {
    if (activeFilterId === null) return false;
    const preset = savedFilters.find((savedFilter) => savedFilter.id === activeFilterId);
    if (preset === undefined) return false;

    const sortArray = (values: readonly string[]) => [...values].sort();
    return (
      preset.filters.balanceMin !== currentFilters.balanceMin ||
      preset.filters.balanceMax !== currentFilters.balanceMax ||
      JSON.stringify(sortArray(preset.filters.strategyType)) !==
        JSON.stringify(sortArray(currentFilters.strategyType)) ||
      JSON.stringify(sortArray(preset.filters.strategy)) !== JSON.stringify(sortArray(currentFilters.strategy)) ||
      JSON.stringify(sortArray(preset.filters.feeSchedule)) !== JSON.stringify(sortArray(currentFilters.feeSchedule)) ||
      JSON.stringify(sortArray(preset.filters.household)) !== JSON.stringify(sortArray(currentFilters.household)) ||
      JSON.stringify(sortArray(preset.filters.advisorIds ?? [])) !==
        JSON.stringify(sortArray(currentFilters.advisorIds)) ||
      (preset.filters.includeUnassignedAdvisor ?? false) !== currentFilters.includeUnassignedAdvisor
    );
  }, [activeFilterId, savedFilters, currentFilters]);

  const hasFilters =
    !isNil(currentFilters.balanceMin) ||
    !isNil(currentFilters.balanceMax) ||
    currentFilters.strategyType.length > 0 ||
    currentFilters.strategy.length > 0 ||
    currentFilters.feeSchedule.length > 0 ||
    currentFilters.household.length > 0 ||
    currentFilters.advisorIds.length > 0 ||
    currentFilters.includeUnassignedAdvisor;

  const activePreset = savedFilters.find((savedFilter) => savedFilter.id === activeFilterId) ?? null;

  const handleSaveFilter = useCallback(
    (name: string) => {
      const savedFilter = saveFilter(name, currentFilters);
      if (savedFilter === null) return;
      setActiveFilterId(savedFilter.id);
      setActiveAccountFilterPresetId(savedFilter.id);
    },
    [saveFilter, currentFilters],
  );

  const handleUpdateFilter = useCallback(
    (id: string) => {
      updateFilter(id, currentFilters);
      setActiveFilterId(id);
      setActiveAccountFilterPresetId(id);
    },
    [updateFilter, currentFilters],
  );

  const loadSavedFilter = useCallback(
    (id: string) => {
      const preset = savedFilters.find((savedFilter) => savedFilter.id === id);
      if (preset === undefined) return;

      setBalanceRange(preset.filters.balanceMin, preset.filters.balanceMax);
      setStrategyType([...preset.filters.strategyType]);
      setStrategy([...preset.filters.strategy]);
      setFeeSchedule([...preset.filters.feeSchedule]);
      setHousehold([...preset.filters.household]);
      setAdvisorIds([...(preset.filters.advisorIds ?? [])]);
      setIncludeUnassignedAdvisor(preset.filters.includeUnassignedAdvisor ?? false);
      setActiveFilterId(preset.id);
      setActiveAccountFilterPresetId(preset.id);
    },
    [
      savedFilters,
      setBalanceRange,
      setStrategyType,
      setStrategy,
      setFeeSchedule,
      setHousehold,
      setAdvisorIds,
      setIncludeUnassignedAdvisor,
    ],
  );

  const clearSavedFilterSelection = useCallback(() => {
    setBalanceRange(null, null);
    setStrategyType([]);
    setStrategy([]);
    setFeeSchedule([]);
    setHousehold([]);
    setAdvisorIds([]);
    setIncludeUnassignedAdvisor(false);
    setActiveFilterId(null);
    setActiveAccountFilterPresetId(null);
  }, [
    setBalanceRange,
    setStrategyType,
    setStrategy,
    setFeeSchedule,
    setHousehold,
    setAdvisorIds,
    setIncludeUnassignedAdvisor,
  ]);

  useEffect(() => {
    if (activeFilterId === null) return;

    const preset = savedFilters.find((savedFilter) => savedFilter.id === activeFilterId);
    if (preset !== undefined) return;

    setActiveFilterId(null);
    setActiveAccountFilterPresetId(null);
  }, [activeFilterId, savedFilters]);

  const accountsExport = useAccountsExport(accounts);

  if (!isNil(error)) {
    return <p className="text-destructive">Error loading accounts: {error.message}</p>;
  }

  return (
    <div className="border border-light rounded-md overflow-hidden">
      <AccountsTableHeader
        selectedColumns={selectedColumns}
        onColumnsChange={handleColumnsChange}
        onExport={() => accountsExport.setModalOpen(true)}
        savedFilterProps={{
          atLimit,
          activePresetName: activePreset?.name ?? null,
          isModified,
          hasFilters,
          existingNames: savedFilters.map((savedFilter) => savedFilter.name),
          savedFilters,
          onSave: handleSaveFilter,
          onUpdate: handleUpdateFilter,
        }}
        resultCount={{
          value: totalCount,
          singularLabel: 'account',
          pluralLabel: 'accounts',
          isLoading: loading,
        }}
      />
      {savedFilters.length > 0 && (
        <div className="px-6">
          <SavedAccountFilterChips
            savedFilters={savedFilters}
            activeFilterId={activeFilterId}
            isModified={isModified}
            onLoadPreset={(preset) => loadSavedFilter(preset.id)}
            onClearPreset={clearSavedFilterSelection}
            onRename={(id, newName) => renameFilter(id, newName)}
            onDelete={(id) => {
              const remaining = deleteFilter(id);
              if (activeFilterId === id || remaining.length === 0) {
                setActiveFilterId(null);
                setActiveAccountFilterPresetId(null);
              }
            }}
          />
        </div>
      )}
      <AccountsDataTable queryFrag={data} loading={loading} />
      <DataTableExportModal
        open={accountsExport.open}
        onOpenChange={accountsExport.onOpenChange}
        columns={accountsExport.columns}
        formats={accountsExport.formats}
        selectedColumnIds={accountsExport.selectedColumnIds}
        selectedFormat={accountsExport.selectedFormat}
        state={accountsExport.state}
        availableColumns={accountsExport.availableColumns}
        selectedColumns={accountsExport.selectedColumns}
        canExport={accountsExport.canExport}
        setSelectedFormat={accountsExport.setSelectedFormat}
        toggleColumn={accountsExport.toggleColumn}
        addColumn={accountsExport.addColumn}
        removeColumn={accountsExport.removeColumn}
        reset={accountsExport.reset}
        execute={accountsExport.execute}
      />
    </div>
  );
}
